import { useState, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { persistUserSession, updateAuthTokenFromResponse } from "../../utils/authSession";
import { appStorage } from "../../utils/appStorage";

const redirectAfterVerification = (navigate, { status, user, verificationType } = {}) => {
    if (status === "4") {
        navigate("/wait-list", { replace: true });
        return;
    }

    if (verificationType === "3") {
        appStorage.setItem("sms_verified", user?.sms_verified?.toString() ?? "1");
        navigate("/contact", { replace: true });
        return;
    }

    const paymentPending =
        user?.payment_status === 0 ||
        user?.payment_status === "0" ||
        appStorage.getItem("payment_status") === "0";

    if (status === "5" || paymentPending) {
        navigate("/payment", { replace: true });
    } else {
        navigate("/contact", { replace: true });
    }
};

const redirectToPaymentForSms = (navigate, message) => {
    appStorage.setItem("sms_verified", "0");
    appStorage.setItem("verification_type", "3");
    toast.dismiss();
    navigate("/payment", { replace: true });
};

export default function Otp() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: "",
        otp: new Array(6).fill(""),
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [resendTimer, setResendTimer] = useState(30);
    const [canResend, setCanResend] = useState(false);
    const otpInputs = useRef([]);

    const verificationType = appStorage.getItem("verification_type");
    const isPhoneVerification = verificationType === "3";

    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
        setCanResend(true);
    }, [resendTimer]);

    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return false;

        const newOtp = [...formData.otp];
        newOtp[index] = element.value;
        setFormData((prev) => ({ ...prev, otp: newOtp }));

        if (element.value !== "" && element.nextSibling) {
            element.nextSibling.focus();
        }
    };

    const handleOtpKeyDown = (e, index) => {
        if (e.key === "Backspace" && e.target.value === "" && index > 0) {
            otpInputs.current[index - 1].focus();
        }
    };

    const handleSubmitOtp = async (e) => {
        e.preventDefault();

        if (formData.otp.some((digit) => digit === "")) {
            setErrors({ otp: "Please enter the complete code" });
            return;
        }

        const email = appStorage.getItem("email");
        const phone = appStorage.getItem("phone_number");
        const code = appStorage.getItem("country_code");

        if (!verificationType) {
            toast.dismiss();
            toast.error("Verification type not found. Please login again.");
            return;
        }

        if (verificationType === "3" && (!phone || !code)) {
            toast.dismiss();
            toast.error("Phone details not found. Please sign up or login again.");
            return;
        }

        setErrors({});
        setLoading(true);

        try {
            let apiUrl = "";
            let payload;

            if (verificationType === "2") {
                apiUrl = `${process.env.REACT_APP_API_BASE_URL}/verify-email`;
                payload = new URLSearchParams({
                    email: email,
                    otp: formData.otp.join(""),
                });
            } else if (verificationType === "3") {
                apiUrl = `${process.env.REACT_APP_API_BASE_URL}/verify-phone`;
                payload = new URLSearchParams({
                    country_code: code,
                    phone_number: phone,
                    otp: formData.otp.join(""),
                });
            }

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: payload.toString(),
            });

            const data = await response.json();
            const status = data.status ?? data.Status;
            const message = data.message ?? data.Message;
            const row = Array.isArray(data.data) ? data.data[0] : data.data;
            const user = row?.user || row || data.user;

            if (status === "0" || status === 0) {
                toast.dismiss();
                toast.error(message || "Invalid OTP");
                setErrors({ otp: message || "Invalid OTP" });
                return;
            }

            if (status === "5" || status === 5) {
                redirectToPaymentForSms(navigate, message);
                return;
            }

            const success =
                status === "1" ||
                status === 1 ||
                status === "3" ||
                status === 3 ||
                data.Status === 1 ||
                data.Status === "1";

            if (!success) {
                toast.dismiss();
                toast.error(message || "Invalid OTP");
                setErrors({ otp: message || "Invalid OTP" });
                return;
            }

            updateAuthTokenFromResponse(data, user, row);

            persistUserSession({
                ...user,
                plan_status: user?.plan_status ?? row?.plan_status ?? data.plan_status,
            });
            toast.success(message || "OTP Verified Successfully! 🎉");
            redirectAfterVerification(navigate, { status, user, verificationType });
        } catch (error) {
            toast.dismiss();
            toast.error("Something went wrong!");
            setErrors({ otp: error.message });
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        const email = appStorage.getItem("email");
        const phone = appStorage.getItem("phone_number");
        const code = appStorage.getItem("country_code");

        if (!verificationType) return;

        let apiUrl = "";
        let payload;

        if (verificationType === "2") {
            apiUrl = `${process.env.REACT_APP_API_BASE_URL}/resend-verify-email-otp`;
            payload = new URLSearchParams({ email });
        } else if (verificationType === "3") {
            if (!phone || !code) {
                toast.error("Phone details not found. Please sign up or login again.");
                return;
            }
            apiUrl = `${process.env.REACT_APP_API_BASE_URL}/resend-phone-otp`;
            payload = new URLSearchParams({
                country_code: code,
                phone_number: phone,
                send_via: "sms",
            });
        }

        try {
            setLoading(true);

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: payload.toString(),
            });

            const data = await response.json().catch(() => ({}));
            const status = data.status ?? data.Status;

            if (status === "5" || status === 5) {
                redirectToPaymentForSms(navigate, data.message);
                return;
            }

            const resendOk =
                response.ok &&
                (status === "1" ||
                    status === 1 ||
                    status === "3" ||
                    status === 3 ||
                    data.Status === 1 ||
                    data.Status === "1" ||
                    status == null);

            if (resendOk) {
                toast.success(
                    isPhoneVerification
                        ? "SMS code sent successfully!"
                        : "OTP resent successfully!"
                );
                setResendTimer(30);
                setCanResend(false);
            } else {
                toast.dismiss();
                toast.error(data.message || "Failed to resend OTP");
            }
        } catch (error) {
            toast.dismiss();
            toast.error("Something went wrong!");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleResendOtp();
    }, []);

    return (
        <div className="min-h-[var(--app-height,100dvh)] overflow-y-auto overscroll-contain bg-[#F3F0FF] flex items-start sm:items-center justify-center px-4 py-6 sm:py-8">
            <div className="w-full max-w-[520px] p-6 sm:p-8 md:p-10">
                <div className="flex flex-col items-center gap-4 mb-8">
                    <div className="h-20 w-20 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#6A35FF] flex items-center justify-center shadow-md">
                        <img src="./logo.svg" alt=" PhoneBookPlus Logo" />
                    </div>

                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-500 font-inter">
                         PhoneBookPlus
                    </h1>
                </div>

                <div className="space-y-3 mb-3">
                    <div className="space-y-5">
                        {(() => {
                            const email = appStorage.getItem("email");
                            const phone = appStorage.getItem("phone_number");
                            const code = appStorage.getItem("country_code");

                            if (verificationType === "3" && phone) {
                                return (
                                    <p className="text-[#475467] font-medium text-center">
                                        Sign in code has been sent to{" "}
                                        <span className="font-semibold">
                                            {code} {phone}
                                        </span>
                                        . Check your SMS to continue.
                                    </p>
                                );
                            }
                            if (verificationType === "2" && email) {
                                return (
                                    <p className="text-[#475467] font-medium text-center">
                                        Sign in code has been sent to{" "}
                                        <span className="font-semibold">{email}</span>. Check your
                                        email to continue.
                                    </p>
                                );
                            }
                            return (
                                <p className="text-[#475467] font-medium text-center">
                                    Sign in code has been sent. Please check your email or phone.
                                </p>
                            );
                        })()}
                    </div>
                </div>

                <form className="space-y-5" onSubmit={handleSubmitOtp}>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                            Enter Verification Code
                        </label>
                        <div className="flex justify-between space-x-2">
                            {formData.otp.map((data, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength="1"
                                    value={data}
                                    ref={(ref) => (otpInputs.current[index] = ref)}
                                    onChange={(e) => handleOtpChange(e.target, index)}
                                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                                    onFocus={(e) => e.target.select()}
                                    className="w-12 h-12 rounded-xl border border-gray-400 bg-white text-center text-lg font-semibold text-gray-800 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15 shadow-sm"
                                />
                            ))}
                        </div>
                        {errors.otp && <p className="text-red-500 text-xs mt-1">{errors.otp}</p>}
                    </div>

                    <div className="text-center">
                        <p className="text-sm text-gray-600">
                            Haven&apos;t received the sign in code?{" "}
                            {canResend ? (
                                <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    className="text-[#7C4DFF] hover:underline font-medium"
                                >
                                    Resend it
                                </button>
                            ) : (
                                <span className="text-gray-500">Resend in {resendTimer}s</span>
                            )}
                        </p>
                    </div>

                    <button
                        type="submit"
                        disabled={loading || formData.otp.some((digit) => digit === "")}
                        className="w-full rounded-3xl bg-gradient-to-b from-[#7c4ff9] to-[#47299c] py-3.5 text-white font-semibold shadow-lg shadow-[#7C4DFF]/25 hover:brightness-105 active:scale-[0.99] transition disabled:opacity-60"
                    >
                        Submit
                    </button>
                </form>
            </div>
        </div>
    );
}
